type EventMapConstraint = object;
type EventHandler<TPayload> = (payload: TPayload) => void;

/**
 * The application's event contract. BL-88 (S115-F11) — this is an EMPTY seed
 * owned by `core`; the concrete, entity-typed payloads are merged in from the
 * `game` layer (`src/game/appEventMap.ts`) via module augmentation, so `core`
 * imports NOTHING from `entities`/`systems` (the locked arrow direction) while
 * every `new EventBus()` still defaults to the fully-typed map. The interface is
 * intentionally extensible (declaration-merged), so it is not `Record`-narrowed.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AppEventMap {}

/**
 * Minimal pub/sub event bus for decoupled communication.
 * Systems emit events, UI subscribes. No direct cross-references.
 */
export class EventBus<TEventMap extends EventMapConstraint = AppEventMap> {
  private listeners = new Map<keyof TEventMap, Set<EventHandler<unknown>>>();
  // BL-265: depth of active emit() calls. The old code allocated a defensive
  // `[...handlers]` snapshot on EVERY emit (incl. the hottest per-hit combat
  // events) purely to stay safe if a handler subscribes/unsubscribes mid-emit.
  // Instead we iterate the live Set with zero allocation, and mutations that
  // land while an emit is in flight copy-on-write (see `mutate`) so the
  // in-flight iteration keeps walking its original Set — preserving the exact
  // prior semantics (a handler added during emit is NOT seen this emit; one
  // removed during emit IS still called this emit) without the per-emit cost.
  private emitDepth = 0;

  on<K extends keyof TEventMap>(
    event: K,
    handler: EventHandler<TEventMap[K]>,
  ): () => void {
    this.mutate(event, (set) =>
      set.add(handler as EventHandler<unknown>),
    );
    return () => this.off(event, handler);
  }

  emit<K extends keyof TEventMap>(
    event: K,
    ...payload: TEventMap[K] extends void ? [] : [TEventMap[K]]
  ): void {
    const handlers = this.listeners.get(event);
    if (!handlers || handlers.size === 0) return;
    const value = payload[0] as TEventMap[K];
    this.emitDepth++;
    try {
      for (const handler of handlers) {
        try {
          (handler as EventHandler<TEventMap[K]>)(value);
        } catch (e) {
          console.error(`[EventBus] Error in "${String(event)}" handler:`, e);
        }
      }
    } finally {
      this.emitDepth--;
    }
  }

  off<K extends keyof TEventMap>(
    event: K,
    handler: EventHandler<TEventMap[K]>,
  ): void {
    const set = this.listeners.get(event);
    if (!set || !set.has(handler as EventHandler<unknown>)) return;
    this.mutate(event, (s) => s.delete(handler as EventHandler<unknown>));
  }

  clear(): void {
    this.listeners.clear();
  }

  /**
   * Apply a mutation to an event's handler Set. While an emit is iterating
   * (emitDepth > 0) the Set is replaced with a mutated COPY so the in-flight
   * loop keeps walking the original — copy-on-write. When nothing is emitting,
   * the Set is mutated in place (the common case: no allocation).
   */
  private mutate<K extends keyof TEventMap>(
    event: K,
    fn: (set: Set<EventHandler<unknown>>) => void,
  ): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    } else if (this.emitDepth > 0) {
      set = new Set(set);
      this.listeners.set(event, set);
    }
    fn(set);
  }
}
